import React, { useRef, useState } from 'react'
import { Collapse, IconButton, Menu, MenuItem } from '@mui/material'

import {
  ChevronDown as DownIcon,
  ChevronUp as UpIcon,
  Edit as EditIcon,
} from '../../icons'
import i18n from '../../locales/i18n'
import { CSSPropertiesMap, Nil, SignatureOption } from '../../types'

const styles: CSSPropertiesMap = {
  text: {
    margin: 0,
    fontFamily: 'Rubik, sans-serif',
    fontStyle: 'normal',
    fontWeight: '400',
    color: '#3C3838',
    backgroundColor: '#FFFFFF',
  },
  signatureTableBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '300px',
    borderCollapse: 'collapse',
    cursor: 'not-allowed',
    userSelect: 'none',
  },
  signatureCell: {
    border: '1px solid #EDECEC',
  },
  signatureHint: {
    color: '#918D8D',
    marginTop: '8px',
  },
  signatureFixedWidth: {
    width: '206px',
  },
  signatureImage: {
    maxWidth: '100%',
    height: '38px',
    marginLeft: '25px',
  },
  signatureBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  signatureEditBtn: {
    right: 0,
    position: 'absolute',
  },
  strong: {
    fontWeight: 500,
    padding: '8px',
  },
  menu: {
    width: '300px',
  },
}

export interface SignatureViewProps {
  readonly disabled?: boolean
  readonly onEdit?: () => void
  readonly onSelect?: (signatureOption: SignatureOption) => void
  readonly signatureDate?: string
  readonly signatureOptions?: SignatureOption[]
  readonly signatureString: string | Nil
  readonly signerLicence?: string
  readonly signerName?: string
}

const SignatureView = ({
  signatureDate = '',
  signatureString,
  signerLicence,
  signerName,
  disabled,
  onEdit,
  onSelect,
  signatureOptions,
}: SignatureViewProps) => {
  const label = i18n.t('Common:SIGNATURE_VIEW.LABEL')

  const tableRef = useRef<HTMLTableElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLTableElement | null>(null)
  const open = Boolean(anchorEl)
  const handleClick = () => {
    setAnchorEl(tableRef.current)
  }
  const handleClose = (option?: SignatureOption) => {
    if (onSelect && option) {
      onSelect(option)
    }
    setAnchorEl(null)
  }

  return (
    <>
      <table
        ref={tableRef}
        style={{ ...styles.signatureTableBox, ...styles.text }}
      >
        <tbody>
          <tr>
            <td style={{ ...styles.signatureCell, ...styles.strong }}>
              {label}
            </td>
            <td
              style={{ ...styles.signatureCell, ...styles.signatureFixedWidth }}
            >
              <div style={styles.signatureBox}>
                {signatureString && (
                  <img
                    alt={label.toLowerCase()}
                    src={signatureString}
                    style={styles.signatureImage}
                  />
                )}
                {onEdit && (
                  <IconButton
                    color="primary"
                    disabled={disabled}
                    style={styles.signatureEditBtn}
                    onClick={onEdit}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                {onSelect && (
                  <IconButton
                    disabled={disabled}
                    style={styles.signatureEditBtn}
                    onClick={handleClick}
                  >
                    {open ? <UpIcon /> : <DownIcon />}
                  </IconButton>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      {signerName && (
        <div>
          <p style={{ ...styles.text, ...styles.signatureHint }}>
            <>
              {i18n.t('Common:SIGNATURE_VIEW.SIGNED')}
              {signatureDate
                ? ` ${signatureDate} ${i18n.t(
                  'Common:SIGNATURE_VIEW.BY_SIGNER_NAME',
                  { signerName },
                )}`
                : ` ${i18n.t('Common:SIGNATURE_VIEW.BY_SIGNER_NAME', {
                  signerName,
                })}`}
              {signerLicence
                ? ` | ${i18n
                  .t('Common:LICENSE')
                  .toLowerCase()}: ${signerLicence}`
                : ''}
            </>
          </p>
        </div>
      )}
      <Menu
        MenuListProps={{
          style: { ...styles.menu },
        }}
        TransitionComponent={Collapse}
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
      >
        {signatureOptions?.map(option => (
          <MenuItem key={option.id} onClick={() => handleClose(option)}>
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default SignatureView
